import {
  X,
  Warehouse,
  Building2,
  MapPin,
  Phone,
  User,
  Tag,
  Clock,
  Globe,
} from "lucide-react";

// ─── Shared ───────────────────────────────────────────────────────────────────

function DetailShell({ open, onClose, icon: Icon, title, subtitle, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[9999]">
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl ring-1 ring-slate-200">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-100 p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-slate-100 p-2">
                <Icon className="h-5 w-5 text-slate-700" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">{title}</p>
                {subtitle && (
                  <p className="text-xs text-slate-400">{subtitle}</p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-xl p-2 hover:bg-slate-100"
            >
              <X className="h-5 w-5 text-slate-600" />
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 rounded-lg bg-slate-100 p-1.5">
        <Icon className="h-3.5 w-3.5 text-slate-500" />
      </div>
      <div>
        <p className="text-xs text-slate-400">{label}</p>
        <p className="text-sm font-medium text-slate-800">{value}</p>
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
      {children}
    </p>
  );
}

function Badge({ children, color = "slate" }) {
  const colors = {
    slate: "bg-slate-100 text-slate-600",
    emerald: "bg-emerald-50 text-emerald-700",
    sky: "bg-sky-50 text-sky-700",
    teal: "bg-teal-50 text-teal-700",
    orange: "bg-orange-50 text-orange-700",
    blue: "bg-blue-50 text-blue-700",
  };
  return (
    <span
      className={`rounded-lg px-2.5 py-1 text-xs font-semibold ${colors[color]}`}
    >
      {children}
    </span>
  );
}

// ─── Warehouse Detail Modal ───────────────────────────────────────────────────

export function WarehouseDetailModal({ open, onClose, warehouse }) {
  if (!open || !warehouse) return null;

  const fullAddress = [
    warehouse.street,
    warehouse.area,
    warehouse.city,
    warehouse.zipCode,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <DetailShell
      open={open}
      onClose={onClose}
      icon={Warehouse}
      title={warehouse.name}
      subtitle={`Warehouse #${warehouse.id}`}
    >
      <div className="p-4 space-y-5">
        {/* Shipping Mark */}
        {warehouse.shippingMark && (
          <div className="flex items-center gap-2 rounded-xl bg-slate-50 px-4 py-3">
            <Tag className="h-4 w-4 text-slate-400" />
            <span className="text-xs text-slate-500">Shipping Mark</span>
            <span className="ml-auto rounded-lg bg-slate-900 px-2.5 py-1 text-xs font-bold text-white tracking-wider">
              {warehouse.shippingMark}
            </span>
          </div>
        )}

        {/* Contact */}
        <div className="space-y-3">
          <SectionTitle>Contact</SectionTitle>
          <div className="grid grid-cols-2 gap-3">
            <InfoRow
              icon={User}
              label="Contact Person"
              value={warehouse.contactPerson}
            />
            <InfoRow icon={Phone} label="Phone" value={warehouse.phone} />
          </div>
        </div>

        <div className="h-px bg-slate-100" />

        {/* Location */}
        <div className="space-y-3">
          <SectionTitle>Location</SectionTitle>
          <InfoRow
            icon={Globe}
            label="Country"
            value={`${warehouse.country?.name} (${warehouse.country?.code})`}
          />
          <InfoRow icon={MapPin} label="Address" value={fullAddress} />
        </div>

        <div className="h-px bg-slate-100" />

        {/* Used by */}
        <div className="space-y-3">
          <SectionTitle>Used By</SectionTitle>
          {warehouse.agentForCompany ? (
            <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2.5">
              <div>
                <p className="text-xs text-slate-400">Agent Warehouse for</p>
                <p className="text-sm font-medium text-slate-800">
                  {warehouse.agentForCompany.name}
                </p>
              </div>
              <Badge color="emerald">Agent</Badge>
            </div>
          ) : null}

          {warehouse.destinationForCompanies?.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs text-slate-400">
                Destination Warehouse for
              </p>
              {warehouse.destinationForCompanies.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2.5"
                >
                  <p className="text-sm font-medium text-slate-800">{c.name}</p>
                  <Badge color="blue">Destination</Badge>
                </div>
              ))}
            </div>
          ) : null}

          {!warehouse.agentForCompany &&
            (!warehouse.destinationForCompanies ||
              warehouse.destinationForCompanies.length === 0) && (
              <p className="text-sm text-slate-400">
                Not assigned to any company yet.
              </p>
            )}
        </div>

        {/* Footer */}
        <div className="flex justify-end pt-1">
          <button
            onClick={onClose}
            className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
          >
            Close
          </button>
        </div>
      </div>
    </DetailShell>
  );
}

// ─── Company Detail Modal ─────────────────────────────────────────────────────

const typeColor = { AIR: "sky", SEA: "teal", LAND: "orange" };
const modeColor = { EXPRESS: "emerald", STANDARD: "blue", ECONOMY: "slate" };
const containsColor = {
  REGULAR: "slate",
  LIQUID: "blue",
  POWDER: "orange",
  BATTERY: "orange",
};

export function ShippingCompanyDetailModal({ open, onClose, company }) {
  if (!open || !company) return null;

  return (
    <DetailShell
      open={open}
      onClose={onClose}
      icon={Building2}
      title={company.name}
      subtitle={`Company #${company.id}`}
    >
      <div className="p-4 space-y-5 max-h-[75vh] overflow-y-auto">
        {/* Overview badges */}
        <div className="flex items-center gap-2 flex-wrap">
          <Badge color={typeColor[company.type] || "slate"}>
            {company.type}
          </Badge>
          <Badge color={modeColor[company.mode] || "slate"}>
            {company.mode}
          </Badge>
          <div className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-2.5 py-1">
            <Clock className="h-3.5 w-3.5 text-slate-500" />
            <span className="text-xs font-semibold text-slate-600">
              {company.deliveryTimeMin}–{company.deliveryTimeMax} days
            </span>
          </div>
        </div>

        {/* Origin */}
        <div className="space-y-3">
          <SectionTitle>Origin</SectionTitle>
          <InfoRow
            icon={Globe}
            label="Country"
            value={`${company.country?.name} (${company.country?.code})`}
          />
        </div>

        <div className="h-px bg-slate-100" />

        {/* Agent Warehouse */}
        <div className="space-y-3">
          <SectionTitle>Agent Warehouse</SectionTitle>
          {company.agentWarehouse ? (
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-800">
                  {company.agentWarehouse.name}
                </p>
                {company.agentWarehouse.shippingMark && (
                  <span className="rounded-lg bg-slate-900 px-2 py-0.5 text-xs font-bold text-white">
                    {company.agentWarehouse.shippingMark}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <InfoRow
                  icon={User}
                  label="Contact"
                  value={company.agentWarehouse.contactPerson}
                />
                <InfoRow
                  icon={Phone}
                  label="Phone"
                  value={company.agentWarehouse.phone}
                />
              </div>
              <InfoRow
                icon={MapPin}
                label="Address"
                value={[
                  company.agentWarehouse.street,
                  company.agentWarehouse.city,
                  company.agentWarehouse.country?.name,
                ]
                  .filter(Boolean)
                  .join(", ")}
              />
            </div>
          ) : (
            <p className="text-sm text-slate-400">
              No agent warehouse assigned.
            </p>
          )}
        </div>

        <div className="h-px bg-slate-100" />

        {/* Destination Warehouse */}
        <div className="space-y-3">
          <SectionTitle>Destination Warehouse</SectionTitle>
          {company.destinationWarehouse ? (
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-800">
                  {company.destinationWarehouse.name}
                </p>
                {company.destinationWarehouse.shippingMark && (
                  <span className="rounded-lg bg-slate-900 px-2 py-0.5 text-xs font-bold text-white">
                    {company.destinationWarehouse.shippingMark}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <InfoRow
                  icon={User}
                  label="Contact"
                  value={company.destinationWarehouse.contactPerson}
                />
                <InfoRow
                  icon={Phone}
                  label="Phone"
                  value={company.destinationWarehouse.phone}
                />
              </div>
              <InfoRow
                icon={MapPin}
                label="Address"
                value={[
                  company.destinationWarehouse.street,
                  company.destinationWarehouse.city,
                  company.destinationWarehouse.country?.name,
                ]
                  .filter(Boolean)
                  .join(", ")}
              />
            </div>
          ) : (
            <p className="text-sm text-slate-400">
              No destination warehouse assigned.
            </p>
          )}
        </div>

        <div className="h-px bg-slate-100" />

        {/* Prices */}
        <div className="space-y-3">
          <SectionTitle>Prices ({company.prices?.length || 0})</SectionTitle>
          {company.prices?.length > 0 ? (
            <div className="space-y-2">
              {company.prices.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2.5"
                >
                  <div className="flex items-center gap-2">
                    <Badge color={containsColor[p.contains] || "slate"}>
                      {p.contains}
                    </Badge>
                    {p.shipmentType && (
                      <span className="text-xs text-slate-400">
                        ({p.shipmentType})
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-slate-800">
                      {p.rate}{" "}
                      <span className="text-xs font-normal text-slate-400">
                        {p.currency}/{p.unit}
                      </span>
                    </p>
                    {p.minimumCharge && (
                      <p className="text-xs text-slate-400">
                        Min. {p.minimumCharge} {p.currency}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">No prices set yet.</p>
          )}
        </div>
        <div className="h-px bg-slate-100" />

        <div className="space-y-3">
          <SectionTitle>
            Instructions ({company.instructions?.length || 0})
          </SectionTitle>
          {company.instructions?.length > 0 ? (
            <div className="space-y-2">
              {company.instructions.map((ins, i) => (
                <div
                  key={ins.id}
                  className="flex items-start gap-2.5 rounded-xl bg-slate-50 px-3 py-2.5"
                >
                  <span className="mt-0.5 min-w-[16px] text-xs font-bold text-slate-400">
                    {i + 1}.
                  </span>
                  <p className="text-sm text-slate-700">{ins.instruction}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">No instructions added yet.</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end pt-1">
          <button
            onClick={onClose}
            className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
          >
            Close
          </button>
        </div>
      </div>
    </DetailShell>
  );
}
